<script setup lang="ts">
/**
 * The "Promo code" row of the shop. Owns only the text box and its two states
 * (typing / code kept); the parent owns the kept code and any refusal, because the
 * code is checked against each purchase when it is clicked (GoldReserve + promo.ts).
 */
import { ref, computed } from 'vue';
import { t } from '../i18n';
import { minOrderLabel, normalizeCode, isCodeShape, refusalKey } from './promo';

const props = defineProps<{ applied: string; errorKey: string }>();
const emit = defineEmits<{ (e: 'apply', code: string): void; (e: 'remove'): void }>();

const draft = ref('');
const shapeError = ref(false);
const error = computed(() => (shapeError.value ? refusalKey('not_found') : props.errorKey));

function apply() {
  const code = normalizeCode(draft.value);
  shapeError.value = !isCodeShape(code);
  if (shapeError.value) return;
  draft.value = '';
  emit('apply', code);
}

function remove() {
  shapeError.value = false;
  emit('remove');
}

const minOrder = computed(minOrderLabel);
</script>

<template>
  <div class="mb-5 rounded-sm border border-border/70 bg-surface/60 p-3">
    <label for="promo-code" class="block font-label uppercase tracking-wider text-[10px] text-text-dim">
      {{ t('shop.promo.label') }}
    </label>

    <div v-if="applied" class="mt-1 flex flex-wrap items-center justify-between gap-2">
      <span class="font-body text-sm text-gold-bright">
        {{ t('shop.promo.kept', { code: applied }) }}
      </span>
      <button
        type="button"
        class="min-h-[44px] cursor-pointer px-3 touch-manipulation font-label text-xs tracking-wider text-text-dim underline underline-offset-2 hover:text-text"
        @click="remove"
      >{{ t('shop.promo.remove') }}</button>
    </div>

    <form v-else class="mt-1 flex gap-2" @submit.prevent="apply">
      <input
        id="promo-code"
        v-model="draft"
        type="text"
        maxlength="32"
        autocomplete="off"
        autocapitalize="characters"
        spellcheck="false"
        :placeholder="t('shop.promo.placeholder')"
        class="min-h-[44px] min-w-0 flex-1 rounded-sm border border-border bg-bg px-3 font-body text-sm uppercase text-text outline-none focus:border-gold"
        @input="shapeError = false"
      />
      <button
        type="submit"
        class="btn-forge btn-forge--sm min-h-[44px] shrink-0 touch-manipulation"
        :disabled="!draft.trim()"
      >{{ t('shop.promo.apply') }}</button>
    </form>

    <p v-if="error" role="alert" class="mt-1 font-body text-[12px] text-error">
      {{ t(error, { amount: minOrder }) }}
    </p>
    <p v-else class="mt-1 font-body text-[11px] text-text-dim">
      {{ applied ? t('shop.promo.keptHint') : t('shop.promo.hint', { amount: minOrder }) }}
    </p>
  </div>
</template>
